// Field-level feedback mode (tester-only; AgroPlan pattern, fresh build).
// A floating 💬 toggle (bottom-LEFT — bottom-right belongs to the demo chat
// FABs) turns on "fb-mode": every click is captured, the clicked element is
// keyed + labeled, and a small dialog collects a comment that lands in the D1
// `feedback` table with a full page snapshot. Open-comment badges render next
// to the elements they refer to. Unlocked with a 4-digit PIN (httpOnly cookie).
import { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { api } from '../lib/api'
import { useI18n } from '../lib/i18n'

type Target = { key: string; label: string; value: string }
type CountRow = { element_key: string; n: number }

const HOVER_STYLE_ID = 'fb-mode-style'
const HOVER_CSS =
  '.fb-mode *:hover { outline: 2px dashed #ffb21f !important; cursor: crosshair !important }'

function visibleText(el: HTMLElement): string {
  return (el.textContent ?? '').replace(/\s+/g, ' ').trim()
}

// No type predicate here: @cloudflare/workers-types' global Element clashes
// with lib.dom's, which breaks `el is HTMLSelectElement`-style narrowing.
function isFormField(el: HTMLElement): boolean {
  return el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement || el instanceof HTMLSelectElement
}

function fieldValue(el: HTMLElement): string | null {
  if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) return el.value
  if (el instanceof HTMLSelectElement) return el.value
  return null
}

/** Nearest ancestor (incl. self) that has visible text or is a form field. */
function pickTarget(start: HTMLElement): HTMLElement {
  let el: HTMLElement | null = start
  while (el && el !== document.body) {
    if (isFormField(el) || visibleText(el).length > 0) return el
    el = el.parentElement
  }
  return start
}

function cssPath(el: HTMLElement): string {
  const parts: string[] = []
  let node: HTMLElement | null = el
  let depth = 0
  while (node && node !== document.body && depth < 5) {
    const cls = Array.from(node.classList).slice(0, 2).join('.')
    parts.unshift(node.tagName.toLowerCase() + (cls ? `.${cls}` : ''))
    node = node.parentElement
    depth += 1
  }
  return parts.join('>').slice(0, 120)
}

function deriveTarget(el: HTMLElement): Target {
  const withId = el.closest('[id]') as HTMLElement | null
  const text = visibleText(el)
  const key = withId?.id
    ? `#${withId.id}`
    : `${cssPath(el)}::${text.slice(0, 40)}`
  const fieldVal = fieldValue(el)
  const labelText =
    text ||
    el.getAttribute('placeholder') ||
    el.getAttribute('aria-label') ||
    '(no text)'
  const label = `${el.tagName.toLowerCase()}: ${labelText}`.slice(0, 80)
  const value = fieldVal !== null ? fieldVal.slice(0, 200) : text.slice(0, 200)
  return { key, label, value }
}

/** Resolve a stored element_key back to a live element (best effort). */
function resolveKey(key: string): HTMLElement | null {
  if (key.startsWith('#')) return document.getElementById(key.slice(1))
  const [path, textSuffix] = key.split('::')
  const tag = (path.split('>').pop() ?? '').split('.')[0]
  if (!tag) return null
  const candidates = document.querySelectorAll<HTMLElement>(tag)
  for (const el of Array.from(candidates)) {
    if (textSuffix && visibleText(el).startsWith(textSuffix)) return el
  }
  return null
}

function buildSnapshot(author: string): string {
  const forms = Array.from(document.querySelectorAll<HTMLElement>('input,select,textarea'))
    .filter((el) => !(el instanceof HTMLInputElement && el.type === 'password'))
    .map((el) => {
      const field = el as HTMLInputElement
      return {
        name: field.name || field.id || field.getAttribute('placeholder') || '(unnamed)',
        value: String(field.value ?? '').slice(0, 200),
      }
    })
  let lang: string | null = null
  try {
    lang = localStorage.getItem('qordo_lang')
  } catch {
    /* storage blocked */
  }
  return JSON.stringify({
    url: window.location.href,
    pathname: window.location.pathname,
    lang,
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    userAgent: navigator.userAgent,
    forms,
    localStorageKeys: { qordo_lang: lang, fb_author: author },
  })
}

export default function FeedbackMode() {
  const { t } = useI18n()
  const location = useLocation()
  const [enabled, setEnabled] = useState(false)
  // The 💬 button is INVISIBLE to normal visitors. It appears only for a QA
  // tester — someone who arrived via the secret `?qa=<token>` link (which
  // activates silently) or who already has the tester cookie from before.
  const [activated, setActivated] = useState(false)
  const [needPin, setNeedPin] = useState(false)
  const [pin, setPin] = useState('')
  const [pinError, setPinError] = useState('')
  const [target, setTarget] = useState<Target | null>(null)
  const [comment, setComment] = useState('')
  const [author, setAuthor] = useState(() => {
    try {
      return localStorage.getItem('fb_author') ?? 'Djoko'
    } catch {
      return 'Djoko'
    }
  })
  const [saving, setSaving] = useState(false)
  const [toast, setToast] = useState('')
  const badgeHostRef = useRef<HTMLDivElement | null>(null)
  const dialogOpenRef = useRef(false)
  dialogOpenRef.current = target !== null

  const clearBadges = useCallback(() => {
    badgeHostRef.current?.remove()
    badgeHostRef.current = null
  }, [])

  const refreshBadges = useCallback(async () => {
    clearBadges()
    let rows: CountRow[] = []
    try {
      const d = await api.get<{ counts: CountRow[] }>(
        `/api/feedback/counts?page=${encodeURIComponent(window.location.pathname)}`,
      )
      rows = d.counts
    } catch {
      return
    }
    if (!rows.length) return
    const host = document.createElement('div')
    host.setAttribute('data-fb-ui', '1')
    for (const row of rows) {
      const el = resolveKey(row.element_key)
      if (!el) continue
      const rect = el.getBoundingClientRect()
      const badge = document.createElement('div')
      badge.textContent = String(row.n)
      badge.style.cssText =
        'position:absolute;z-index:55;min-width:18px;height:18px;padding:0 4px;' +
        'border-radius:9px;background:#dc2626;color:#fff;font-size:11px;font-weight:700;' +
        'display:flex;align-items:center;justify-content:center;pointer-events:none;' +
        `left:${Math.max(0, rect.right + window.scrollX - 8)}px;top:${Math.max(0, rect.top + window.scrollY - 8)}px`
      host.appendChild(badge)
    }
    document.body.appendChild(host)
    badgeHostRef.current = host
  }, [clearBadges])

  // QA activation, once on mount: (1) if the URL carries `?qa=<token>`, POST it
  // to activate silently (sets the tester cookie) and turn feedback ON, then
  // strip the token from the address bar; (2) else probe — if a tester cookie
  // already exists, reveal the 💬 button (but stay OFF until tapped). Normal
  // visitors match neither and never see anything.
  useEffect(() => {
    let alive = true
    const params = new URLSearchParams(window.location.search)
    const qa = params.get('qa')
    ;(async () => {
      if (qa && qa.length > 8) {
        try {
          await api.post('/api/qa/activate', { token: qa })
          if (!alive) return
          setActivated(true)
          setEnabled(true)
          // Remove the secret token from the URL so it isn't shared/bookmarked.
          params.delete('qa')
          const clean = window.location.pathname + (params.toString() ? `?${params}` : '') + window.location.hash
          window.history.replaceState({}, '', clean)
          return
        } catch {
          /* invalid token → fall through to the cookie probe */
        }
      }
      try {
        const st = await api.get<{ tester: boolean }>('/api/qa/status')
        if (alive && st.tester) setActivated(true) // cookie already valid → show the button
      } catch {
        /* not a tester → stay invisible */
      }
    })()
    return () => {
      alive = false
    }
  }, [])

  // Click capture while fb-mode is on. Never on /qa-admin — that page owns its
  // clicks.
  useEffect(() => {
    if (!enabled || location.pathname.replace(/\/$/, '') === '/qa-admin') return
    document.documentElement.classList.add('fb-mode')
    const style = document.createElement('style')
    style.id = HOVER_STYLE_ID
    style.textContent = HOVER_CSS
    document.head.appendChild(style)

    const onClick = (e: MouseEvent) => {
      const raw = e.target as HTMLElement | null
      if (!raw) return
      // Never capture clicks on our own UI (toggle, dialog, badges).
      if (raw.closest('[data-fb-ui]')) return
      e.preventDefault()
      e.stopPropagation()
      if (dialogOpenRef.current) return // dialog open → swallow, don't retarget
      setTarget(deriveTarget(pickTarget(raw)))
    }
    document.addEventListener('click', onClick, true)
    void refreshBadges()

    return () => {
      document.documentElement.classList.remove('fb-mode')
      document.getElementById(HOVER_STYLE_ID)?.remove()
      document.removeEventListener('click', onClick, true)
      clearBadges()
    }
  }, [enabled, location.pathname, refreshBadges, clearBadges])

  // Route change while enabled → re-fetch badges for the new page.
  useEffect(() => {
    if (enabled) void refreshBadges()
  }, [location.pathname, enabled, refreshBadges])

  async function toggle() {
    if (enabled) {
      setEnabled(false)
      setTarget(null)
      return
    }
    // Probe: 401 → tester cookie missing → ask for the PIN.
    try {
      await api.get(`/api/feedback/counts?page=${encodeURIComponent(window.location.pathname)}`)
      setEnabled(true)
    } catch {
      setNeedPin(true)
    }
  }

  async function unlock() {
    setPinError('')
    try {
      await api.post('/api/feedback/unlock', { pin })
      setNeedPin(false)
      setPin('')
      setEnabled(true)
    } catch {
      setPinError(t('Wrong PIN'))
    }
  }

  async function submit() {
    if (!target || !comment.trim() || saving) return
    setSaving(true)
    try {
      try {
        localStorage.setItem('fb_author', author)
      } catch {
        /* storage blocked */
      }
      await api.post('/api/feedback', {
        element_key: target.key,
        element_label: target.label,
        current_value: target.value,
        comment: comment.trim(),
        author: author.trim() || 'Djoko',
        page: window.location.pathname,
        snapshot_json: buildSnapshot(author),
      })
      setTarget(null)
      setComment('')
      setToast(t('Saved ✓'))
      window.setTimeout(() => setToast(''), 2000)
      void refreshBadges()
    } catch {
      setToast(t('Save failed'))
      window.setTimeout(() => setToast(''), 2500)
    } finally {
      setSaving(false)
    }
  }

  // Normal visitors never activated → render nothing at all (no button, no
  // trace of a feedback layer on the public site). Also never run on the
  // /qa-admin review page — its own buttons must stay clickable.
  if (!activated || location.pathname.replace(/\/$/, '') === '/qa-admin') return null

  return (
    <div data-fb-ui="1">
      <button
        onClick={() => void toggle()}
        title={t('Feedback mode')}
        aria-label={t('Feedback mode')}
        className={`fixed bottom-4 left-4 z-50 h-10 w-10 rounded-full text-lg shadow-lg border transition-colors ${
          enabled
            ? 'bg-amber-500 border-amber-400 text-white'
            : 'bg-ink-900/90 border-ink-700 text-ink-100 hover:border-ink-500'
        }`}
      >
        💬
      </button>

      {toast && (
        <div className="fixed bottom-16 left-4 z-50 rounded-lg bg-emerald-600 px-3 py-2 text-sm font-medium text-white shadow-lg">
          {toast}
        </div>
      )}

      {needPin && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 p-4">
          <div className="w-full max-w-xs rounded-2xl border border-ink-800 bg-ink-900 p-5 text-center">
            <h2 className="mb-1 text-base font-semibold text-white">{t('Tester access')}</h2>
            <p className="mb-4 text-xs text-ink-500">{t('Enter the 4-digit PIN')}</p>
            <input
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
              onKeyDown={(e) => e.key === 'Enter' && void unlock()}
              inputMode="numeric"
              type="password"
              maxLength={4}
              autoFocus
              className="w-28 rounded-lg border border-ink-700 bg-ink-950 px-2 py-2 text-center text-xl tracking-[8px] text-white outline-none focus:border-brand-500"
            />
            {pinError && <p className="mt-2 text-xs text-red-400">{pinError}</p>}
            <div className="mt-4 flex gap-2">
              <button
                onClick={() => {
                  setNeedPin(false)
                  setPin('')
                  setPinError('')
                }}
                className="flex-1 rounded-lg border border-ink-700 py-2 text-sm text-ink-100"
              >
                {t('Cancel')}
              </button>
              <button
                onClick={() => void unlock()}
                className="flex-1 rounded-lg bg-brand-600 py-2 text-sm font-semibold text-white"
              >
                {t('Unlock')}
              </button>
            </div>
          </div>
        </div>
      )}

      {target && (
        <div className="fixed inset-x-0 bottom-0 z-[70] p-3 sm:inset-x-auto sm:left-4 sm:w-96">
          <div className="rounded-2xl border border-amber-500/40 bg-ink-900 p-4 shadow-2xl">
            <div className="mb-2">
              <div className="text-sm font-semibold text-white break-words">{target.label}</div>
              {target.value && (
                <div className="mt-0.5 text-xs text-ink-500 break-words">
                  {t('Current value')}: {target.value}
                </div>
              )}
            </div>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value.slice(0, 2000))}
              placeholder={t('What should change here?')}
              rows={3}
              autoFocus
              className="w-full rounded-lg border border-ink-700 bg-ink-950 p-2 text-sm text-white outline-none focus:border-amber-500"
            />
            <input
              value={author}
              onChange={(e) => setAuthor(e.target.value.slice(0, 60))}
              placeholder={t('Your name')}
              className="mt-2 w-full rounded-lg border border-ink-700 bg-ink-950 px-2 py-1.5 text-sm text-white outline-none focus:border-amber-500"
            />
            <div className="mt-3 flex gap-2">
              <button
                onClick={() => {
                  setTarget(null)
                  setComment('')
                }}
                className="flex-1 rounded-lg border border-ink-700 py-2 text-sm text-ink-100"
              >
                {t('Cancel')}
              </button>
              <button
                onClick={() => void submit()}
                disabled={!comment.trim() || saving}
                className="flex-1 rounded-lg bg-amber-500 py-2 text-sm font-semibold text-white disabled:opacity-50"
              >
                {saving ? '…' : t('Submit')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
